import { cn } from "@faire/prototype-playground/lib/utils";
import { Button } from "@faire/slate/components/button";
import React from "react";

import { PADX } from "../constants";

interface ReferralBannerProps {
  /**
   * Expected to be a light tint. The copy and CTA draw from Slate's text
   * tokens rather than hardcoded white, so a dark value here would fail
   * contrast — a dark band is the thing this replaced, not a mode it keeps.
   */
  backgroundColor?: string;
}

/**
 * Faire's referral incentive for logged-out retailers.
 *
 * An inset card, not the full-bleed olive band it replaced. That band spanned
 * the entire viewport in a promotional colour nothing else on the page used, so
 * it read as an ad sold into the brand's page rather than part of it — the one
 * module a shopper learns to scroll past. Sharing the inset geometry of
 * `PromoHero` and `FaireMarketBanner` puts it back inside the page's own
 * rhythm: the offer is Faire's, the surface stays the brand's.
 *
 * The copy is deliberately not brand-authored. This is Faire's referral
 * programme and its terms are identical for every brand, so it is the one place
 * in this package where fixed copy is correct rather than a hardcoding smell —
 * what varies per brand is the surface it sits on (Ryan, 2026-09-17).
 */
export const ReferralBanner: React.FC<ReferralBannerProps> = ({
  backgroundColor = "var(--slate-color-surface-tertiary)",
}) => (
  <div className={cn("py-8", PADX)}>
    <div
      className={cn(
        "flex flex-col items-center gap-5 rounded-xl p-6 text-center",
        "md:flex-row md:justify-between md:gap-10 md:p-8 md:text-left"
      )}
      style={{ backgroundColor }}
    >
      <div className="flex flex-col gap-1.5">
        {/* The measures keep this a two-line headline over a two-line support
            note instead of one long ribbon of text on a wide window, where the
            CTA would end up marooned from the offer it belongs to. Both lines
            land on "free shipping" at that measure, so it is bound together —
            "shipping." alone on the second line reads as a typo. */}
        <p className="type-fs-section-heading text-fs-text-primary max-w-[44ch]">
          New to Faire? Get 50% off your first order and 1 year of
          free&nbsp;shipping.
        </p>
        <p className="type-fs-paragraph text-fs-text-subdued max-w-[60ch]">
          Claim our referral offer to get up to $150 off your first order with
          us and 1 year of free&nbsp;shipping.
        </p>
      </div>
      <span data-signup-gate className="flex-shrink-0">
        <Button variant="primary">Sign up to buy</Button>
      </span>
    </div>
  </div>
);
